import React from "react";

/**
 * Form — form container (Phase 2b Forms).
 *
 * A thin <form> with a consistent vertical rhythm between Field rows and a
 * Form.Actions sub-component for the button row. Replaces the inline-style
 * `display: flex; flexDirection: column; gap: 12px;` that every form
 * currently re-implements by hand.
 *
 * Density:
 *   - default: var(--space-3) between rows (12px) — standard forms.
 *   - compact: var(--space-2) (8px) — dense admin tables / drawers.
 *
 * Actions:
 *   <Form.Actions>
 *     <Button variant="ghost">Cancel</Button>
 *     <Button type="submit">Save</Button>
 *   </Form.Actions>
 *
 *   Buttons render in source order, right-aligned by default. Pass
 *   `align="left"` or `align="space-between"` for the rare cases where
 *   the layout differs (e.g. destructive-left + primary-right).
 *
 * The Form itself stays unopinionated about submit handling — pass an
 * onSubmit, prevent default in the handler, and treat it as a vanilla
 * <form>. Validation/error display lives on individual <Field>s.
 */

export interface FormProps extends React.FormHTMLAttributes<HTMLFormElement> {
  /** Vertical rhythm between rows. Default: "default" (12px). */
  density?: "default" | "compact";
}

interface FormComponent extends React.FC<FormProps> {
  Actions: React.FC<FormActionsProps>;
}

const Form: FormComponent = ({
  density = "default",
  className = "",
  children,
  ...rest
}) => {
  const cls = [
    "v2-form",
    `v2-form--${density}`,
    className,
  ].filter(Boolean).join(" ");
  return (
    <form className={cls} {...rest}>
      {children}
    </form>
  );
};

export interface FormActionsProps extends React.HTMLAttributes<HTMLDivElement> {
  /** "right" (default), "left", or "space-between". */
  align?: "right" | "left" | "space-between";
}

Form.Actions = function FormActions({
  align = "right",
  className = "",
  children,
  ...rest
}: FormActionsProps) {
  return (
    <div
      className={[
        "v2-form__actions",
        `v2-form__actions--${align}`,
        className,
      ].filter(Boolean).join(" ")}
      {...rest}
    >
      {children}
    </div>
  );
};

export default Form;
